// src/supabase.ts
// Client-side Supabase client using anon/publishable key.
// Replaces Firestore + Storage in src/firebase.js for all data operations.
// Firebase client is kept only for FCM + App Check (src/firebase.js).
//
// Requires env vars (Vite — import.meta.env):
//   VITE_SUPABASE_URL — Supabase project URL
//   VITE_SUPABASE_ANON_KEY — anon/publishable key (safe for client)
//
// Usage:
//   import { supabase, getTenantFilter, getTenantPathSegments } from './supabase';
//   const { data } = await supabase.from('users').select('*').eq('tenant_id', tid); // perf-sweep-ignore: documentation example, not executed

import { createClient, type SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl: string | undefined = import.meta.env.VITE_SUPABASE_URL
  || import.meta.env.NEXT_PUBLIC_SUPABASE_URL;

const supabaseAnonKey: string | undefined = import.meta.env.VITE_SUPABASE_ANON_KEY
  || import.meta.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl) {
  throw new Error('Missing VITE_SUPABASE_URL environment variable');
}

if (!supabaseAnonKey) {
  throw new Error('Missing VITE_SUPABASE_ANON_KEY environment variable');
}

/**
 * Anon-key Supabase client — respects RLS policies.
 * Uses Supabase Auth natively (supabase.auth.*). The Supabase session JWT is
 * attached automatically to every data request, driving Row Level Security.
 */
const _supabaseAnon: SupabaseClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    experimental: { passkey: true },
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

/**
 * Default Supabase client export — always the anon-key client with Supabase Auth JWT for RLS enforcement.
 * The service-role key must never be used in the client bundle.
 */
export const supabase: SupabaseClient = _supabaseAnon;

/** Filter object shape for tenant-scoped queries (`.match()` / `.eq()`). */
export interface TenantFilter {
  tenant_id: string;
}

/**
 * Builds a tenant-scoped filter object.
 * Replaces Firestore getTenantPathSegments() hierarchical paths.
 * Extra args (subPaths) are silently ignored — callers keep the old signature.
 *
 * @param tenantId - The active tenant identifier.
 */
export function getTenantFilter(tenantId: string): TenantFilter {
  if (!tenantId) {
    throw new Error('Tenant ID is required');
  }
  return { tenant_id: tenantId };
}

/**
 * Compatibility wrapper — returns a filter object matching the Firestore
 * getTenantPathSegments() signature.
 *   Old: doc(db, ...getTenantPathSegments(tid, "users", uid))
 *   New: supabase.from('users').select('*').match(getTenantPathSegments(tid)).eq('id', uid) // perf-sweep-ignore: documentation example, not executed
 */
export function getTenantPathSegments(tenantId: string): TenantFilter {
  return getTenantFilter(tenantId);
}

/**
 * Compatibility wrapper — returns a string representation of the tenant path.
 * In Firestore: 'tenants/{tenantId}/users/...'
 * In Supabase: flat tables with tenant_id column.
 */
export function getTenantPath(tenantId: string, ...subPaths: string[]): string {
  return `tenants/${tenantId}/${subPaths.join('/')}`;
}

/**
 * Returns the current Supabase session access token for API authentication.
 * Works in any context (no React hook required). Falls back to null if there
 * is no active Supabase session.
 */
export async function getAuthToken(): Promise<string | null> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.access_token ?? null;
  } catch (e) {
    console.warn('[getAuthToken] Failed to get Supabase session token:', e);
    return null;
  }
}
